/**
 * Where a figure-of-eight circuit crosses itself: one pass stays at ground
 * level and crosses on a short bridge; the other dips into a walled cutting
 * and runs under it (Suzuka's crossover). Shared by the generator (the dip is
 * part of the road's height, so part of the simulation), the terrain, the
 * meshes and the scenery. Plain arithmetic only.
 */
export interface Crossing {
  /** Sample nearest the crossing on the pass that goes over (the bridge) and under. */
  over: number;
  under: number;
  /** Arc length of the crossing point along each pass. */
  sOver: number;
  sUnder: number;
  /** The crossing point. */
  x: number;
  y: number;
  /** |sin| of the angle between the passes (1 = square). */
  sin: number;
  /** How far the lower road drops below the ground at the crossing, metres. */
  depth: number;
  /** Length of each ramp down and back up, and of the level stretch between them. */
  ramp: number;
  flat: number;
  /** From the lower road's centreline to the face of the cutting's walls. */
  cutHalf: number;
  /** From the upper road's centreline to its parapets on the bridge. */
  deckHalf: number;
}

export interface SegmentCrossing {
  /** Segment indices (i < j) and the parameter along each where they meet. */
  i: number;
  j: number;
  ti: number;
  tj: number;
  x: number;
  y: number;
}

/** Every place a closed polyline crosses itself. */
export function findCrossings(px: number[], py: number[]): SegmentCrossing[] {
  const n = px.length;
  const out: SegmentCrossing[] = [];
  for (let i = 0; i < n; i++) {
    const i2 = (i + 1) % n;
    const ax = px[i];
    const ay = py[i];
    const rx = px[i2] - ax;
    const ry = py[i2] - ay;
    for (let j = i + 2; j < n; j++) {
      const j2 = (j + 1) % n;
      if (j2 === i) continue;
      const cx = px[j];
      const cy = py[j];
      const sx = px[j2] - cx;
      const sy = py[j2] - cy;
      const den = rx * sy - ry * sx;
      if (den === 0) continue;
      const ti = ((cx - ax) * sy - (cy - ay) * sx) / den;
      const tj = ((cx - ax) * ry - (cy - ay) * rx) / den;
      if (ti <= 0 || ti >= 1 || tj <= 0 || tj >= 1) continue;
      out.push({ i, j, ti, tj, x: ax + rx * ti, y: ay + ry * ti });
    }
  }
  return out;
}

/** Shortest distance between two arc positions on a loop of length L. */
export function arcDist(a: number, b: number, L: number): number {
  let d = Math.abs(a - b) % L;
  if (d > L - d) d = L - d;
  return d;
}

/** C2 step from 0 (x <= 0) to 1 (x >= 1). */
function smootherstep(x: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  return x * x * x * (x * (x * 6 - 15) + 10);
}

/** How far below the ground the lower road is at arc length s (0 away from the underpass). */
export function crossingDip(c: Crossing, s: number, length: number): number {
  const u = arcDist(s, c.sUnder, length);
  if (u <= c.flat / 2) return c.depth;
  return c.depth * (1 - smootherstep((u - c.flat / 2) / c.ramp));
}

/** Half the length of track the underpass takes, ramps included. */
export function underpassHalf(c: Crossing): number {
  return c.flat / 2 + c.ramp;
}

/** Half the length of the upper road that is bridge (over the cutting, walls included). */
export function bridgeHalf(c: Crossing): number {
  return (c.cutHalf + 1.2) / c.sin + c.deckHalf * Math.sqrt(Math.max(0, 1 - c.sin * c.sin)) / c.sin + 1.5;
}

interface TrackLike {
  n: number;
  length: number;
  px: number[];
  py: number[];
  s: number[];
  elev: number[];
}

/**
 * "Is this point down in the cutting?" for a crossover: nearest point on the
 * lower road's underpass stretch, its lateral offset and how deep the cut is
 * there. Brute force over that stretch's samples (a couple of hundred).
 */
export class CuttingQuery {
  private idx: number[] = [];

  constructor(
    private t: TrackLike,
    readonly c: Crossing,
  ) {
    const half = underpassHalf(c);
    for (let i = 0; i < t.n; i++) if (arcDist(t.s[i], c.sUnder, t.length) <= half) this.idx.push(i);
  }

  /** Nearest point on the underpass stretch: arc length, |lateral offset|, cut depth and road height there. */
  project(x: number, y: number): { s: number; lat: number; dip: number; floor: number } | null {
    const t = this.t;
    let best = Infinity;
    let bi = -1;
    let bt = 0;
    for (const i of this.idx) {
      const j = (i + 1) % t.n;
      const dx = t.px[j] - t.px[i];
      const dy = t.py[j] - t.py[i];
      const l2 = dx * dx + dy * dy;
      let u = l2 > 0 ? ((x - t.px[i]) * dx + (y - t.py[i]) * dy) / l2 : 0;
      if (u < 0) u = 0;
      else if (u > 1) u = 1;
      const ex = x - (t.px[i] + dx * u);
      const ey = y - (t.py[i] + dy * u);
      const d2 = ex * ex + ey * ey;
      if (d2 < best) {
        best = d2;
        bi = i;
        bt = u;
      }
    }
    if (bi < 0) return null;
    const j = (bi + 1) % t.n;
    const seg = (j === 0 ? t.length : t.s[j]) - t.s[bi];
    const s = t.s[bi] + seg * bt;
    const floor = t.elev[bi] + (t.elev[j] - t.elev[bi]) * bt;
    return { s, lat: Math.sqrt(best), dip: crossingDip(this.c, s, t.length), floor };
  }

  /** Inside the cutting, within `margin` metres beyond its walls' faces. */
  inside(x: number, y: number, margin = 0): boolean {
    const p = this.project(x, y);
    return !!p && p.dip > 0.02 && p.lat <= this.c.cutHalf + margin;
  }
}
